import React from 'react';
import { convertNumberToWords, formatNumberWithCommas, getAbbreviationForPi } from '../../utilities';

const formatDate = (timestamp) =>
  new Date(timestamp * 1000)
    .toLocaleString('en-GB', {
      timeZone: 'Asia/Dhaka',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
    .replace(/\//g, '.');

const isBlank = (value) => value === null || value === undefined || String(value).trim() === '';

// Generic inline-editable wrapper. `as` lets you pick the tag (span/div/td/etc).
const EditableField = ({ editMode, as: Tag = 'span', style, children, ...rest }) => (
  <Tag
    contentEditable={editMode}
    suppressContentEditableWarning={true}
    style={{
      outline: editMode ? '1px dashed #3b82f6' : 'none',
      backgroundColor: editMode ? 'rgba(59,130,246,0.06)' : 'transparent',
      minWidth: editMode ? '10px' : undefined,
      cursor: editMode ? 'text' : 'inherit',
      ...style,
    }}
    {...rest}
  >
    {children}
  </Tag>
);

// copyNumber: 1 -> "1ST COPY", "FIRST" exchange / "SECOND" tenor
// copyNumber: 2 -> "2ND COPY", "SECOND" exchange / "FIRST" tenor
const BillOfExchange = ({ commercial_info, editMode = false, copyNumber = 1 }) => {
  const copyLabel = copyNumber === 1 ? <>1<sup>ST</sup> COPY</> : <>2<sup>ND</sup> COPY</>;
  const exchangeWord = copyNumber === 1 ? 'FIRST' : 'SECOND';
  const tenorWord = copyNumber === 1 ? 'SECOND' : 'FIRST';

  const fields = [
    { label: 'Number of Amendment:', value: commercial_info.amendmentInfo },
    {
      label: 'DATED:',
      value: !isBlank(commercial_info.amendmentDate) ? formatDate(commercial_info.amendmentDate) : null,
    },
    {
      label: 'EXPORT SALES CONTRACT NO:',
      value: !isBlank(commercial_info.contractNo)
        ? `${commercial_info.contractNo}, DATED: ${formatDate(commercial_info.contractDate)}`
        : null,
    },
    { label: 'HS CODE NO:', value: '4819.10.00' },
    { label: 'APPLICANT BIN:', value: commercial_info.partyBin },
    { label: 'APPLICANT BOND LIC. NO.:', value: commercial_info.partyBondLicNo },
    { label: 'APPLICANT BOI NO.:', value: commercial_info.partyBoiNo },
    { label: 'BANK BIN:', value: commercial_info.partyBankBin },
    { label: 'APPLICANT ERC:', value: commercial_info.partyErc },
    { label: 'APPLICANT IRC:', value: commercial_info.partyIrc },
    { label: 'BENEFICIARY BIN:', value: '000686131-1205' },
  ].filter((field) => !isBlank(field.value));

  return (
    <>
      <style>{`
        @media print {
          [contenteditable] {
            outline: none !important;
            background-color: transparent !important;
          }
        }
      `}</style>

      <div style={{ textAlign: 'right', fontWeight: 'bold', fontSize: '14px' }}>
        <EditableField editMode={editMode}>{copyLabel}</EditableField>
      </div>

      <div style={{ textAlign: 'center' }}>
        <EditableField
          editMode={editMode}
          as="h1"
          style={{
            textDecoration: 'underline',
            fontWeight: 'bold',
            marginTop: '5px',
            marginBottom: '20px',
            fontSize: '23px',
            color: '#000',
            display: 'inline-block',
          }}
        >
          BILL OF EXCHANGE
        </EditableField>
      </div>

      <div style={{ textAlign: 'left', fontSize: '14px', lineHeight: '21px' }}>
        <EditableField editMode={editMode} as="div">
          <strong>INVOICE NO:</strong> {commercial_info.invoiceNo}, DATE-{formatDate(commercial_info.commercialDate)}
        </EditableField>

        <EditableField editMode={editMode} as="div" style={{ marginTop: '10px' }}>
          <strong>DRAWN UNDER CREDIT NO.:</strong> {commercial_info.lcNo}, DATED: {formatDate(commercial_info.lcDate)}, {' '}
          {fields.map((field, index) => (
            <React.Fragment key={field.label}>
              <strong>{field.label}</strong> {field.value}
              {index < fields.length - 1 ? ', ' : '.'}
            </React.Fragment>
          ))}
        </EditableField>
      </div>

      <div style={{ textAlign: 'left', padding: '14px 0', fontSize: '14px', lineHeight: '21px' }}>
        <strong>PI NO:</strong>{' '}
        <EditableField editMode={editMode}>
        {commercial_info.piInfo.map((pi, index) => (
          <span key={pi.piId}>
            <strong>{pi.piNo || `PI/${getAbbreviationForPi(commercial_info.partyName)}/${pi.jobNumber}`}</strong> DT.{formatDate(pi.createdDate)}
            {index < commercial_info.piInfo.length - 1 ? ', ' : ''}
          </span>
        ))}
          .
        </EditableField>
      </div>

      <div style={{ textAlign: 'left', padding: '0 0 14px', fontSize: '14px' }}>
        <strong>TRADE TERM:</strong>{' '}
        <EditableField editMode={editMode}>
          CPT APPLICANT'S FACTORY (INCOTERM 2020)
        </EditableField>
      </div>

      <table width="100%" border="0" cellSpacing="0" cellPadding="0" style={{ fontSize: '14px', lineHeight: '21px' }}>
        <tbody>
          <tr>
            <td style={{ width: '140px', verticalAlign: 'top' }}>
              <strong>NOTIFIED TO:</strong>
            </td>
            <td style={{ verticalAlign: 'top' }}>
              <EditableField editMode={editMode} as="div">
                <strong>{commercial_info.partyBank}</strong>,<br />
                {commercial_info.partyBankAddress}
              </EditableField>
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ textAlign: 'left', padding: '24px 0 14px', fontSize: '14px' }}>
        EXCHANGE FOR:{' '}
        <strong>
          <EditableField
            editMode={editMode}
            style={{ textDecoration: 'underline', fontSize: '19px', display: 'inline-block' }}
          >
            US$ = {formatNumberWithCommas(commercial_info.totalPrice)}
          </EditableField>
        </strong>
      </div>

      <div style={{ textAlign: 'left', fontSize: '14px', lineHeight: '21px' }}>
        <EditableField editMode={editMode} as="div">
          DRAFTS AT <strong>{!commercial_info.duration ? 'SIGHT' : `${commercial_info.duration} DAYS`}</strong> PAY
          THIS <strong>{exchangeWord}</strong> EXCHANGE (<strong>{tenorWord}</strong> OF THE TENOR AND DATE BEING UNPAID) TO THE
          ORDER OF <strong>{commercial_info.companyBank}</strong>, {commercial_info.companyBankAddress}
        </EditableField>
      </div>

      <div style={{ textAlign: 'left', padding: '14px 0', fontSize: '14px' }}>
        <strong>TOTAL IN WORD:</strong>{' '}
        <EditableField editMode={editMode}>
          US DOLLAR ({convertNumberToWords(commercial_info.totalPrice).toUpperCase()} ONLY)
        </EditableField>
      </div>

      <div style={{ textAlign: 'left', padding: '14px 0', fontSize: '14px', lineHeight: '21px' }}>
        <strong>TO,</strong>
        {/* <br />
        <br /> */}
        <EditableField editMode={editMode} as="div">
          {commercial_info.partyBank}
          <br />
          {commercial_info.partyBankAddress}
        </EditableField>
      </div>

      <div style={{ textAlign: 'left', padding: '14px 0', fontSize: '14px', lineHeight: '21px' }}>
        <br />
        <EditableField editMode={editMode} as="div">
          {commercial_info.partyName}
          <br />
          {commercial_info.partyAddress}
        </EditableField>
      </div>

      <div style={{ textAlign: 'right', marginTop: '70px', fontSize: '14px', visibility: 'hidden' }}>
        For, <strong>
          <EditableField editMode={editMode} style={{ display: 'inline-block' }}>
            Miah and Sons Industries Limited
          </EditableField>
        </strong>
      </div>

      <div style={{ textAlign: 'right', marginTop: '40px', fontSize: '14px', visibility: 'hidden' }}>
        <strong>
          <EditableField editMode={editMode} style={{ display: 'inline-block' }}>
            Authorize Signature
          </EditableField>
        </strong>
      </div>
    </>
  );
};

export default BillOfExchange;